import { useRef, useCallback } from 'react';
import { useNodeActions } from './useNodeActions';
import { findConnectedNodes } from './utils/graphTraversal';

/**
 * useFlowchartCoreNodeActions Hook
 * 
 * Handles node actions including creating connected nodes,
 * inserting nodes on edges, and auto-tidy after insert.
 */
export function useFlowchartCoreNodeActions({
  tasks,
  interaction,
  findEmptySpaceNearNode,
  findEmptySpaceNearPoint,
  onCreateConnectedTask,
  onInsertNodeOnEdge,
  onInsertMilestoneOnEdge,
  handleAutoLayout,
  followTaskAfterLayout = null,
  isFocusMode,
  focusedTaskIds,
  allFocusedTaskIds,
  addToFocusedTasks,
}) {
  // Refs to hold current values for the insert complete callback
  // This avoids stale closure issues when the callback is called after a delay
  const tasksRef = useRef(tasks);
  const handleAutoLayoutRef = useRef(handleAutoLayout);
  tasksRef.current = tasks;
  handleAutoLayoutRef.current = handleAutoLayout;

  // Callback to trigger auto-tidy after inserting a node on an edge
  // This ensures the chain stays tidy with no overlaps
  // Uses refs to always get the latest tasks and handleAutoLayout
  // Records the combined undo action (insert + position restoration) after tidy completes
  const handleInsertComplete = useCallback((newTaskId, fromTaskId, toTaskId, insertData) => {
    // Use refs to get the latest values (after React state has updated)
    const currentTasks = tasksRef.current;
    const currentHandleAutoLayout = handleAutoLayoutRef.current;
    
    // Find all nodes connected to the affected nodes (the entire chain)
    const connectedNodes = findConnectedNodes(new Set([newTaskId, fromTaskId, toTaskId]), currentTasks);
    
    // Capture original positions BEFORE clearing them - these will be used by undo
    const originalPositions = {};
    connectedNodes.forEach(nodeId => {
      const task = currentTasks.find(t => t.id === nodeId);
      if (task && task.x !== undefined && task.y !== undefined) {
        originalPositions[nodeId] = { x: task.x, y: task.y };
      }
    });
    
    // Clear custom positions for the connected nodes so tidy can recalculate from scratch
    // This ensures the layout algorithm has full control over positioning
    interaction.setCustomPositions(prev => {
      const newPositions = { ...prev };
      connectedNodes.forEach(nodeId => {
        delete newPositions[nodeId];
      });
      return newPositions;
    });
    
    // Small delay to let the position clear propagate, then trigger auto-layout
    // Pass skipRecordAction: true so this tidy doesn't create a separate undo action
    setTimeout(() => {
      currentHandleAutoLayout(connectedNodes, { skipRecordAction: true });
      
      // After tidy completes, record the combined undo action
      // This includes both the node insertion AND the position restoration
      if (insertData?.recordCombinedAction) {
        // Small delay to let the tidy positions settle
        setTimeout(() => {
          insertData.recordCombinedAction(originalPositions);
        }, 100);
      }
    }, 50);
  }, [interaction]); // Only depends on interaction for setCustomPositions

  const handleCreateComplete = useCallback((newTaskId) => {
    // Aim the camera at the tidy slot before the card slides, so the view
    // centers the landing place instead of the handle.
    if (newTaskId) followTaskAfterLayout?.(newTaskId);
    // One ELK tidy from the current on-screen positions. Do not wipe custom
    // positions first (that snaps the board) and do not pass a connected-node
    // subset (that uses the legacy packer and fights ELK, especially when
    // milestones sit on the same chain).
    handleAutoLayoutRef.current?.(null, { skipRecordAction: true });
  }, [followTaskAfterLayout]);

  const { setTaskPosition, handleCreateConnectedNode, handleInsertNodeOnEdge } = useNodeActions({
    interaction,
    findEmptySpaceNearNode,
    findEmptySpaceNearPoint,
    onCreateConnectedTask,
    onInsertNodeOnEdge,
    onInsertMilestoneOnEdge,
    onInsertComplete: handleInsertComplete,
    onCreateComplete: handleCreateComplete,
    isFocusMode,
    focusedTaskIds,
    allFocusedTaskIds,
    addToFocusedTasks,
  });

  return {
    setTaskPosition,
    handleCreateConnectedNode,
    handleInsertNodeOnEdge,
  };
}

export default useFlowchartCoreNodeActions;
